import { useState } from 'react';
import { labelBatchColor, RoastBatch } from '../../api/client';

const ROAST_LEVELS: { label: string; color: string; rgb: [number, number, number]; score: number }[] = [
  { label: 'Coffee 1',  color: '#748060', rgb: [116, 128,  96], score: 5  },
  { label: 'Coffee 2',  color: '#917D60', rgb: [145, 125,  96], score: 15 },
  { label: 'Coffee 3',  color: '#846A52', rgb: [132, 106,  82], score: 25 },
  { label: 'Coffee 4',  color: '#745642', rgb: [116,  86,  66], score: 35 },
  { label: 'Coffee 5',  color: '#5E4231', rgb: [ 94,  66,  49], score: 45 },
  { label: 'Coffee 6',  color: '#483227', rgb: [ 72,  50,  39], score: 55 },
  { label: 'Coffee 7',  color: '#332621', rgb: [ 51,  38,  33], score: 65 },
  { label: 'Coffee 8',  color: '#221D1B', rgb: [ 34,  29,  27], score: 75 },
  { label: 'Coffee 9',  color: '#1B1817', rgb: [ 27,  24,  23], score: 85 },
  { label: 'Coffee 10', color: '#141414', rgb: [ 20,  20,  20], score: 95 },
];

function interpolateRgb(score: number): [number, number, number] {
  const clamped = Math.max(0, Math.min(100, score));
  const t = (clamped / 100) * (ROAST_LEVELS.length - 1);
  const lo = Math.floor(t);
  const hi = Math.min(ROAST_LEVELS.length - 1, lo + 1);
  const f = t - lo;
  const a = ROAST_LEVELS[lo].rgb;
  const b = ROAST_LEVELS[hi].rgb;
  return [
    Math.round(a[0] + f * (b[0] - a[0])),
    Math.round(a[1] + f * (b[1] - a[1])),
    Math.round(a[2] + f * (b[2] - a[2])),
  ];
}

function nearestLevel(score: number) {
  return ROAST_LEVELS.reduce((best, l) =>
    Math.abs(l.score - score) < Math.abs(best.score - score) ? l : best
  );
}

interface Props {
  batch: RoastBatch;
  onClose: () => void;
  onSaved: (updated: RoastBatch) => void;
}

export default function ColorLabelModal({ batch, onClose, onSaved }: Props) {
  const [score, setScore] = useState(44);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rgb = interpolateRgb(score);
  const level = nearestLevel(score);
  const colorCss = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  const gradient = ROAST_LEVELS.map((l) => l.color).join(', ');

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await labelBatchColor(batch.id, level.label, rgb);
      onSaved(updated);
    } catch {
      setError('שגיאה בשמירה, נסה שנית');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <h3 className="text-xl font-bold text-gray-800 mb-1 text-center">תייג צבע קלייה</h3>
        <p className="text-sm text-gray-500 text-center mb-6">{batch.blend_name}</p>

        {/* Big score number */}
        <div className="flex items-center justify-center mb-4 gap-4">
          <div
            className="w-14 h-14 rounded-xl border border-gray-300 shadow-md transition-colors duration-150"
            style={{ backgroundColor: colorCss }}
          />
          <div className="text-center">
            <div className="text-6xl font-extrabold text-gray-900 tabular-nums leading-none">
              {score}
            </div>
            <div className="text-sm font-semibold text-amber-700 mt-1">{level.label}</div>
          </div>
        </div>

        {/* Gradient bar */}
        <div
          className="relative h-5 rounded-full mb-1 border border-gray-200 shadow-inner"
          style={{ background: `linear-gradient(to left, ${gradient})` }}
        >
          {/* Thumb indicator on bar */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full border-2 border-gray-600 shadow transition-all duration-100"
            style={{ left: `${score}%`, transform: 'translate(-50%, -50%)' }}
          />
        </div>

        {/* Slider */}
        <input
          type="range"
          min={0}
          max={100}
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          className="w-full accent-amber-700 cursor-pointer"
          style={{ direction: 'ltr' }}
        />

        <div className="flex justify-between text-xs text-gray-400 mb-5 px-0.5">
          <span>ירוק</span>
          <span>איטלקי</span>
        </div>

        {error && <p className="text-red-600 text-sm text-center mb-3">{error}</p>}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-base font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200"
          >
            ביטול
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 text-base font-bold text-white bg-amber-700 rounded-xl hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </div>
    </div>
  );
}
